//-------------CORE-------------//
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
//-------------MUI-------------//
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
//----------EXTERNAL COMPS--------//
import devicesApi, { useGetDevicesQuery, useMoveDevicesMutation, useMoveDevicesCsvMutation } from "services/devices";
import { statusOf } from "./deviceStatus";

// the server takes at most 500 devices per move; a bigger selection goes in parts of this size
const CHUNK = 200;
const CSV_MAX_BYTES = 2 * 1024 * 1024;
// the import file's columns, so one file works for both (the serial is optional here)
const CSV_TEMPLATE = "imei,serial,solution,name,assetType,latitude,longitude\r\n";
// the Move CSV file's columns, as the Upload CSV tab explains them: [column, needed, what goes in it, example]
const CSV_GUIDE = [
  ["imei", "Yes", "15 digits; the device must already be in Xelerate, in a solution your role holds", "867929065923536"],
  ["solution", "Yes", "The solution the device moves to: its name or id (one your role holds); rows can name different solutions", "Salesforce Munich"],
  ["serial", "No", "A check that the right unit is meant: if the platform has a serial for the device it must be the same, otherwise it is saved to the device", "2024010080"],
  ["name", "No", "New device name; empty keeps the name", "Lobby West"],
  ["assetType", "If the solution has several", "Asset type name or id; empty keeps the device's own when it stays in its solution", "Air Monitor"],
  ["latitude, longitude", "No (both or neither)", "New position; empty keeps the position", "51.5072, -0.1276"],
];
// what the list reads for each device (the table's columns only)
const SELECT = { name: 1, serviceId: 1, internalId: 1, associatedGroups: 1, "packetFromPlatform.c8y_Mobile": 1, "packetFromPlatform.c8y_Hardware": 1, "packetFromPlatform.c8y_Availability": 1 };
const SEARCH_FIELDS = ["name", "serialNo", "imei", "iccid", "internalId"];
const emptyResult = () => ({ moved: [], updated: [], unchanged: [], failed: [] });

// Moves devices that are already in Xelerate to another solution: pick them from a searchable,
// paged list, or upload a CSV file (checked row by row before anything moves). A move is Edit
// Device's move: the device leaves its groups and its parent/child links, and takes on the new
// solution's rules, zones and schedules. Only offered to Device Management at full access.
export default function MoveDevices({ open, onClose }) {
  const token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const solutions = useSelector((state) => state.metaData?.services) || [];
  const nameOf = (id) => (solutions.find((s) => s.id == id) || {}).name || "-";
  const [tab, setTab] = useState("xelerate");
  const [serviceId, setServiceId] = useState("");
  const [assetType, setAssetType] = useState("");
  const target = solutions.find((s) => s.id == serviceId);
  const assets = target?.assets || [];
  const needsAssetType = assets.length > 1 && !assetType;

  // ---- the devices in Xelerate -----------------------------------------------------------------
  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [moveDevices] = useMoveDevicesMutation();
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(tempSearch.trim());
    }, 600);
    return () => clearTimeout(timer);
  }, [tempSearch]);
  // a solution the role sees through one group shows only that group's devices (as on the page)
  const groupIds = solutions.filter((s) => s.group?.id).map((s) => `${s.id}:${s.group.id}`);
  const listArgs = (currentPage, size, select = SELECT) => ({
    token,
    group: JSON.stringify(from ? [from] : solutions.map((s) => s.id)),
    params: `&pageSize=${size}&currentPage=${currentPage}&withTotalPages=true&sort=${JSON.stringify({ name: 1 })}&select=${JSON.stringify(select)}&search=${encodeURIComponent(
      search
    )}&searchFields=${JSON.stringify(SEARCH_FIELDS)}${groupIds.length ? `&associatedGroup=${JSON.stringify(groupIds)}` : ""}`,
  });
  const list = useGetDevicesQuery(listArgs(page + 1, pageSize), { skip: !open || !solutions.length });
  const rows = list.currentData?.payload?.data || [];
  const total = list.currentData?.payload?.totalDocuments || 0;

  async function selectAllMatching() {
    setError("");
    try {
      const ids = [];
      for (let p = 1, pages = 1; p <= pages; p++) {
        // a one-off read: subscribe, take the result, let go of it
        const request = dispatch(devicesApi.endpoints.getDevices.initiate(listArgs(p, 500, { internalId: 1 }), { forceRefetch: true }));
        let answer;
        try {
          answer = await request.unwrap();
        } finally {
          request.unsubscribe();
        }
        ids.push(...(answer?.payload?.data || []).map((d) => d.internalId));
        pages = answer?.payload?.totalPages || 0;
      }
      setSelected((prev) => new Set([...prev, ...ids]));
    } catch (err) {
      setError(err?.data?.message || "Could not select the matching devices");
    }
  }
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const pageSelected = rows.length > 0 && rows.every((d) => selected.has(d.internalId));
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((d) => (pageSelected ? next.delete(d.internalId) : next.add(d.internalId)));
      return next;
    });

  async function moveSelected() {
    const ids = [...selected];
    const summary = emptyResult();
    let stoppedAt = ids.length;
    setError("");
    setResult(null);
    setProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += CHUNK) {
      const response = await moveDevices({ token, body: { devices: ids.slice(i, i + CHUNK), serviceId, platformDeviceType: assetType || undefined } });
      if (response.error) {
        setError(`${response.error.data?.message || "Move failed"}${i ? ` (after ${i} of ${ids.length} devices)` : ""}`);
        stoppedAt = i;
        break;
      }
      const part = response.data.payload;
      Object.keys(summary).forEach((k) => summary[k].push(...(part[k] || [])));
      setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
    }
    setProgress(null);
    if (Object.values(summary).some((v) => v.length)) setResult(summary);
    // what is left to do stays selected: the devices not tried after a failed part, and the failed
    setSelected(new Set([...ids.slice(stoppedAt), ...summary.failed.map((d) => d.id)]));
  }

  // ---- the CSV upload --------------------------------------------------------------------------
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [csvReport, setCsvReport] = useState(null);
  const [validated, setValidated] = useState(false);
  const [moveCsv, csvState] = useMoveDevicesCsvMutation();
  const [showFormat, setShowFormat] = useState(true);
  useEffect(() => setValidated(false), [csvText]);
  async function pickFile(e) {
    const picked = e.target.files && e.target.files[0];
    e.target.value = "";
    setCsvReport(null);
    if (!picked) return;
    if (picked.size > CSV_MAX_BYTES) {
      setFile(null);
      setCsvText("");
      setCsvReport({ error: "The file is larger than 2 MB." });
      return;
    }
    setFile(picked);
    setCsvText(await picked.text());
  }
  async function sendCsv(dryRun) {
    setCsvReport(null);
    const response = await moveCsv({ token, body: { csv: csvText, dryRun } });
    if (response.error) {
      const body = response.error.data || {};
      setShowFormat(false);
      setCsvReport({ error: body.message || "The file could not be checked", formatErrors: body.payload?.errors, rows: body.payload?.rows });
      return;
    }
    const body = response.data;
    setShowFormat(false);
    // a dry run lists every row, so the moves can be read before they happen
    setCsvReport({ message: body.message, result: dryRun ? null : body.payload, plan: dryRun ? body.payload?.rows : null });
    setValidated(dryRun);
    if (!dryRun) {
      setFile(null);
      setCsvText("");
    }
  }
  function downloadTemplate() {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" }));
    link.download = "Device move template.csv";
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  // ---- shared pieces ---------------------------------------------------------------------------
  // labelOf: how a device is named in the summary (the CSV tab names devices by IMEI)
  const outcome = (summary, labelOf = (id) => id) => (
    <Alert severity={summary.failed.length ? "warning" : "success"} style={{ marginBottom: "10px" }}>
      {summary.moved.length} moved
      {summary.updated.length ? `, ${summary.updated.length} updated in their solution` : ""}
      {summary.unchanged.length ? `, ${summary.unchanged.length} already in the solution` : ""}
      {summary.failed.length ? `, ${summary.failed.length} failed` : ""}.
      {summary.failed.slice(0, 20).map((d) => (
        <div key={d.id} style={{ fontSize: "12px" }}>
          {labelOf(d.id)}: {d.reason}
        </div>
      ))}
    </Alert>
  );

  return (
    <Dialog open={open} onClose={progress || csvState.isLoading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Move Devices</DialogTitle>
      <Tabs value={tab} onChange={(e, value) => setTab(value)} style={{ padding: "0 24px" }}>
        <Tab value="xelerate" label="Devices in Xelerate" />
        <Tab value="csv" label="Upload CSV" />
      </Tabs>
      <DialogContent style={{ height: "65vh", display: "flex", flexDirection: "column", gap: "10px", paddingTop: "15px" }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {tab === "xelerate" ? (
          <>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "5px" }}>
              <div style={{ display: "flex", gap: "10px", minWidth: 0, flex: "1 1 360px" }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="move-target">Move to solution *</InputLabel>
                  <Select
                    labelId="move-target"
                    label="Move to solution *"
                    value={serviceId}
                    onChange={(e) => {
                      setServiceId(e.target.value);
                      setAssetType("");
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                  >
                    {solutions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {assets.length > 1 ? (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="move-asset-type">Asset type *</InputLabel>
                    <Select labelId="move-asset-type" label="Asset type *" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
                      {assets.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </div>
              <FormControl size="small" style={{ flex: "0 1 240px" }}>
                <InputLabel id="move-from" shrink>
                  Now in
                </InputLabel>
                <Select
                  labelId="move-from"
                  label="Now in"
                  value={from}
                  displayEmpty
                  notched
                  onChange={(e) => {
                    setPage(0);
                    setFrom(e.target.value);
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                >
                  <MenuItem value="">All solutions</MenuItem>
                  {solutions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search"
                placeholder="Name, serial, IMEI, ICCID, ID"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                style={{ flex: "1 1 260px" }}
              />
            </div>
            {result ? outcome(result) : null}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minHeight: "30px", fontSize: "13px", color: "#555" }}>
              <span id="move-selection">{selected.size} selected</span>
              {total > 0 ? (
                <Button size="small" onClick={selectAllMatching}>
                  Select all {total} matching
                </Button>
              ) : null}
              {selected.size ? (
                <Button size="small" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              ) : null}
            </div>
            {list.isFetching ? <LinearProgress /> : null}
            {list.isError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => list.refetch()}>
                    Retry
                  </Button>
                }
              >
                {list.error?.data?.message || "Could not read the devices"}
              </Alert>
            ) : null}
            <TableContainer style={{ flex: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        id="move-select-page"
                        checked={pageSelected}
                        indeterminate={!pageSelected && rows.some((d) => selected.has(d.internalId))}
                        onChange={togglePage}
                        disabled={!rows.length}
                      />
                    </TableCell>
                    {["Name", "Solution", "ID", "IMEI", "Serial Number", "Status"].map((label) => (
                      <TableCell key={label} style={{ whiteSpace: "nowrap" }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((d) => {
                    const st = statusOf(d.packetFromPlatform?.c8y_Availability?.status);
                    return (
                      <TableRow key={d.internalId} hover onClick={() => toggle(d.internalId)} style={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.has(d.internalId)} />
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{String(d.name || "").trim() || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{nameOf(d.serviceId)}</TableCell>
                        <TableCell>{d.internalId}</TableCell>
                        <TableCell>{d.packetFromPlatform?.c8y_Mobile?.imei || "-"}</TableCell>
                        <TableCell>{d.packetFromPlatform?.c8y_Hardware?.serialNumber || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap", color: st.color }}>{st.label}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!list.isFetching && !list.isError && !rows.length ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" style={{ color: "#999", padding: "40px" }}>
                        {search || from ? "No devices match." : "No devices yet."}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={Math.min(page, Math.max(Math.ceil(total / pageSize) - 1, 0))}
              onPageChange={(e, value) => setPage(value)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onRowsPerPageChange={(e) => {
                setPage(0);
                setPageSize(Number(e.target.value));
              }}
            />
            {progress ? (
              <div>
                <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
                <p style={{ fontSize: "12px", color: "#555" }}>
                  Moving {progress.done} of {progress.total} devices ...
                </p>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "5px" }}>
              <Button variant="outlined" component="label">
                {file ? file.name : "Choose CSV file"}
                <input id="move-csv-file" type="file" accept=".csv,text/csv" hidden onChange={pickFile} />
              </Button>
              <Button onClick={downloadTemplate}>Download template</Button>
              <Button id="move-csv-format-toggle" onClick={() => setShowFormat((v) => !v)}>
                {showFormat ? "Hide file format" : "Show file format"}
              </Button>
            </div>
            {showFormat ? (
              <div id="move-csv-format">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Column</TableCell>
                      <TableCell>Needed</TableCell>
                      <TableCell>What goes in it</TableCell>
                      <TableCell>Example</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {CSV_GUIDE.map(([column, needed, what, example]) => (
                      <TableRow key={column}>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <b>{column}</b>
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{needed}</TableCell>
                        <TableCell>{what}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap", color: "#777" }}>{example}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ul style={{ fontSize: "13px", color: "#555", margin: "10px 0 0", paddingLeft: "20px", lineHeight: 1.7 }}>
                  <li>The same columns as the import file, so one file works for both; the first row names the columns (any order, capitals ignored).</li>
                  <li>A device that moves leaves its groups and its parent/child links, and takes on the new solution's rules, zones and schedules.</li>
                  <li>UTF-8 text, comma-separated, up to 1,000 devices per file. Validate checks every row first; if any row has a problem, nothing moves.</li>
                </ul>
              </div>
            ) : null}
            {csvState.isLoading ? <LinearProgress /> : null}
            {csvReport?.message ? (
              csvReport.result ? (
                outcome(csvReport.result, (id) => ((csvReport.result.rows || []).find((r) => r.deviceId === id) || {}).imei || id)
              ) : (
                <Alert severity="success">{csvReport.message}</Alert>
              )
            ) : null}
            {csvReport?.plan?.length ? (
              <TableContainer id="move-csv-plan" style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>IMEI</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Now in</TableCell>
                      <TableCell>Move to</TableCell>
                      <TableCell>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvReport.plan.map((r) => (
                      <TableRow key={r.line}>
                        <TableCell>{r.line}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{r.imei}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{r.name || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{r.from || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{r.solution || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{{ move: "Move", update: "Update in place", none: "No change" }[r.change] || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {csvReport?.error ? <Alert severity="error">{csvReport.error}</Alert> : null}
            {csvReport?.formatErrors?.length ? (
              <TableContainer style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Problem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvReport.formatErrors.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.line || "-"}</TableCell>
                        <TableCell>{e.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {csvReport?.rows?.length ? (
              <TableContainer style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>IMEI</TableCell>
                      <TableCell>Move to</TableCell>
                      <TableCell>Problems</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvReport.rows
                      .filter((r) => r.errors.length)
                      .map((r) => (
                        <TableRow key={r.line}>
                          <TableCell>{r.line}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{r.imei || "-"}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{r.solution || "-"}</TableCell>
                          <TableCell>{r.errors.join("; ")}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!progress || csvState.isLoading}>
          Close
        </Button>
        {tab === "xelerate" ? (
          <Button
            id="move-selected"
            variant="contained"
            color="secondary"
            onClick={moveSelected}
            disabled={!serviceId || needsAssetType || !selected.size || !!progress}
          >
            Move {selected.size || ""} {selected.size === 1 ? "device" : "devices"}
          </Button>
        ) : (
          <>
            <Button id="move-csv-validate" onClick={() => sendCsv(true)} disabled={!csvText || csvState.isLoading}>
              Validate
            </Button>
            <Button id="move-csv-move" variant="contained" color="secondary" onClick={() => sendCsv(false)} disabled={!csvText || !validated || csvState.isLoading}>
              Move
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
